/**
 * ObjectDisplay Controller
 *
 * @name ips.ac.controller.scfld.ObjectDisplay
 * @public
 */
import BaseController, { Authorisation } from "ips/ac/controller/scfld/BaseController";
import ODataMixin from "ips/ac/controller/scfld/mixin/ODataMixin";
import HelperMethods from "ips/ac/controller/scfld/mixin/HelperMethods";
import formatter from "ips/ac/model/formatter";
import CONSTANTS from "ips/ac/util/Constants";
import PdfService from "ips/ac/data/file/service/PdfService";
import FileService from "ips/ac/data/file/service/FileService";
import Util from "ips/ac/util/Util";
import ODataService from "ips/ac/base/service/ODataService";
import HttpUtil from "ips/ac/util/HttpUtil";
import DefaultFileDetails from "ips/ac/control/FileUpload/DefaultFileDetails";
import Component from "ips/ac/Component";
import { Button$PressEvent } from "sap/m/Button";
import { URLHelper } from "sap/m/library";
import PDFViewer from "sap/m/PDFViewer";
import Event from "sap/ui/base/Event";
import Control from "sap/ui/core/Control";
import XMLView from "sap/ui/core/mvc/XMLView";
import HashChanger from "sap/ui/core/routing/HashChanger";
import { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import JSONModel from "sap/ui/model/json/JSONModel";
import Context from "sap/ui/model/odata/v4/Context";
import ODataModel from "sap/ui/model/odata/v4/ODataModel";
import { ODataContextBinding$DataReceivedEvent } from "sap/ui/model/odata/v4/ODataContextBinding";
import ObjectPageLayout, { ObjectPageLayout$NavigateEvent } from "sap/uxap/ObjectPageLayout";
import ObjectPageSection from "sap/uxap/ObjectPageSection";

/** displayCategoryIds of the FileDetails fragment (ips.ac.data.file.fragment.FileDetails) */
const FILE_DETAILS_CATEGORY_IDS = ["FILE_CATEGORY", "FILE_CATEGORY_PD"];

interface InstanceActions {
  grant: boolean;
  edit: boolean;
  delete: boolean;
}

interface BindingConfiguration {
  parameters?: Record<string, unknown>;
  expand?: string;
}

/** controller of the invisible FileDetails view used for the object header image */
interface FileUploadController {
  fileUpload(): void;
}

/**
 * ObjectDisplay controller that globally handles server side error states
 *
 * @class
 *
 * @author UNIORG
 * @version 1.0.0
 *
 * @constructor
 * @public
 * @extends ips.ac.controller.scfld.ODataController
 * @alias ips.ac.controller.scfld.ObjectDisplay
 * @namespace ips.ac.controller.scfld
 */
export default class ObjectDisplay extends BaseController {
  public formatter = formatter;

  public oAppModel: JSONModel;
  public oObjectPage: ObjectPageLayout;
  protected _oObjectPage: ObjectPageLayout;
  protected _oViewModel: JSONModel;
  protected _oODataModel: ODataModel;
  protected _oAuthorisation: Authorisation;
  protected _oBindingConfiguration: BindingConfiguration;
  protected _sEntitySet: string;
  protected _sEntityType: string;
  protected _sCreateRoute: string;
  protected _sPath: string;
  protected _sGuid: string;
  protected _sRoutedHash: Record<string, ObjectPageSection>;

  /* =========================================================== */
  /* lifecycle methods                                           */

  /* =========================================================== */

  constructor(sName?: string) {
    super(sName);

    // add used mixins
    ODataMixin.addTo(this);
    HelperMethods.addTo(this);
    //FormMixin.addTo(this);
  }

  public onInit(): void {
    const oSettings = this._oSettings;

    // manifest config settings
    // this._sRouteName = "route"; // set in BaseController
    this._sEntitySet = this._sEntitySet || oSettings.entitySet;
    this._sCreateRoute = oSettings.createRoute || this._sCreateRoute;
    this._sRoutedHash = {};

    this.oAppModel = this.getOwnerComponent().getModel("app") as JSONModel;

    // Model used to manipulate control states. The chosen values make sure,
    // detail page is busy indication immediately so there is no break in
    // between the busy indication for loading the view's meta data
    const oViewModel = new JSONModel({
      routeName: this._sRouteName,
      entitySet: this._sEntitySet,
      entityType: null, // set by odata metadata
      viewTitle: null, // set by odata metadata
      enableCreate: false,
      messagesLength: null,
      busy: false,
      delay: 0,
    });
    this.setModel(oViewModel, "ui");
    this._oViewModel = oViewModel;

    // make core SmartValueHelp (SVH) model available for language selection
    this.setModel(Util.getModel("SVH"), "SVH");

    // apply authorisation
    this._oAuthorisation = this._getAuthorisation(oSettings);
    this._oViewModel.setProperty("/Authorisation", this._oAuthorisation);
    this._oViewModel.setProperty("/InstanceAction", {});

    this.oObjectPage = this.getView().byId("ObjectPageLayout") as ObjectPageLayout;

    // oData model
    this._oODataModel = this.getODataModel();
    // attach after meta data loaded
    this._applyServiceMetadata();

    // apply additional binding parameters
    this._applyAdditionalBindingParameters();

    if (this._sRouteName) {
      this.getRouter().getRoute(this._sRouteName)?.attachPatternMatched(this._onObjectMatched, this);
    }
  }

  /**
   * Event handler (attached declaratively) called when the add button in the master view is pressed. it opens the create view.
   * @public
   */

  public onCopy(oEvent: Button$PressEvent): void {
    const oDefaultData = this.getView().getBindingContext().getObject() as Record<string, unknown>;
    const sFromTarget = this._sRouteName;
    let sEditRoute = oEvent.getSource().data("routeName") as string;
    if (!sEditRoute) {
      sEditRoute = sFromTarget + "edit"; // Use edit route with copy fla;
    }
    const sLayout = this._getComponent().getRouteLayout(sEditRoute);
    const sFromLayout = this._oSettings.layout;

    // Make a clean copy of the data to avoid modifying the original
    const oCopyData = JSON.parse(JSON.stringify(oDefaultData)) as Record<string, unknown>;

    // Clean the data using our helper function if available
    if (typeof this._cleanCopyData === "function") {
      this._cleanCopyData(oCopyData);
    } else {
      // Fallback cleaning if function is not available
      const fieldsToClean = [
        "Guid",
        "ImageFileGuid",
        "Tag",
        "CreatedBy",
        "CreatedOn",
        "UpdatedBy",
        "UpdatedOn",

        "AddressGuid",
        "ContainerGuid",
        "PartnerGuid",
        "IssuedToClientGuid",
        "IssuedToUserGuid",

        "@odata.context",

        "CreatedBy_ClientGuid",
        "CreatedBy_UserFirstname",
        "CreatedBy_UserLastname",
        "CreatedBy_ClientTitle",

        "CountNoteDetails",
      ];

      fieldsToClean.forEach((field) => {
        if (field in oCopyData) {
          delete oCopyData[field];
        }
      });

      // Clean navigation properties
      [
        "OrderDetails",
        "NoteDetails",
        "FileDetails",
        "ComponentDetails",
        "ProductDetails",
        "InstanceActionDetails",
        "BuildingDiaryDetails",
        "DocumentationDetails",
        "ContactDetails",
        "SelfActivityDetails",
      ].forEach((property) => {
        if (property in oCopyData) {
          delete oCopyData[property];
        }
      });
    }

    // console.log("Cleaned copy data:", oCopyData);

    const oConfig = {
      layout: sLayout,
      mode: "create",
      entityDefaults: oCopyData,
      fromTarget: sFromTarget,
      fromLayout: sFromLayout,
      copy: true, // Flag for copy operations
    };

    // console.log("Copy operation config:", oConfig);

    this.showRoute(sEditRoute, oConfig);
  }

  /**
   * Event handler (attached declaratively) called when the add button in the master view is pressed. it opens the create view.
   * @public
   */
  public onCopyDeep(): void {
    const oDefaultData = this.getView().getBindingContext().getObject() as object,
      sFromTarget = this._sRouteName,
      sEditRoute = sFromTarget + "copydeep",
      sLayout = this._getComponent().getRouteLayout(sEditRoute),
      sFromLayout = this._oSettings.layout;

    //this.getModel("appView").setProperty("/addEnabled", false);
    //this.getRouter().getTargets().display(this._sRouteName + "edit", oDefaultData);
    const oConfig = {
      layout: sLayout,
      mode: "create",
      entityDefaults: oDefaultData,
      fromTarget: sFromTarget,
      fromLayout: sFromLayout,
      copy: true,
      path: this.getView().getBindingContext().getPath().replace("/", ""),
    };
    this.showRoute(sEditRoute, oConfig);
  }

  /**
   * Event handler (attached declaratively) for the view edit button. Open a view to enable the user update the selected item.
   * @param {sap.ui.base.Event} oEvent - the navigate to event.
   * @public
   */
  public onEdit(oEvent: Button$PressEvent): void {
    const sFromTarget = this._sRouteName;

    let sEditRoute = oEvent.getSource().data("routeName") as string;
    if (!sEditRoute) {
      sEditRoute = sFromTarget + "edit";
    }

    let sLayout: string = this._getComponent().getRouteLayout(sEditRoute);
    const sFromLayout = this._oSettings.layout;

    if (!sLayout) {
      sLayout = this._getComponent()
        .getHelper()
        .getNextUIState(2).layout;
    }

    const bUseRouting = CONSTANTS.GENERAL.USE_ROUTING as boolean,
      oConfig = {
        layout: sLayout,
        mode: "update",
        //path: encodeURIComponent(sPath.substr(1)),
        path: encodeURIComponent(this._sPath.substring(1)),
        fromTarget: sFromTarget,
        fromLayout: sFromLayout,
      };


    if (bUseRouting) {
      //this._oStorage.put(sEditRoute, oConfig);
      this.setRouteDisplayData(sEditRoute, oConfig);
      this.navTo(sEditRoute, oConfig);
    } else {
      this.display(sEditRoute, oConfig);
      this._getComponent().setLayout(sLayout);
    }
  }

  public onPressSupport(oEvent: Event): void {
    const oObject = (oEvent.getSource() as Control).getBindingContext().getObject() as { SupportLink?: string };
    let sUrl = oObject["SupportLink"];
    if (sUrl && sUrl.length > 0) {
      sUrl = sUrl.replace(/ /g, "");
      if (!sUrl.startsWith("http://") && !sUrl.startsWith("https://")) {
        sUrl = "https://" + sUrl;
      }
      URLHelper.redirect(sUrl, true);
    }
  }

  /**
   * Event handler to view file attachments using PDFReader or open inside new browser window
   *
   * @param {sap.ui.base.Event} oEvent - the navigate to event.
   * @public
   */
  public onOpenFileViewer(oEvent: Event): void {
    const oSource = oEvent.getSource() as Control,
      oContext = oSource.getBindingContext().getObject() as { Guid: string };

    this.openFileViewer(this.formatter.fileUrl.call(this, oContext.Guid) as string);
  }

  /**
   * data:referenceId="{Guid}"
   * data:title="{Description}"
   *
   * @param {sap.ui.base.Event} oEvent - the navigate to event.
   * @public
   */
  public async onPDFViewer(oEvent: Button$PressEvent): Promise<void> {
    try {
      this.setAppBusy();
      const oSource = oEvent.getSource();
      const sInstanceGuid = oSource.data("referenceId") as string;
      const sInstanceType = oSource.data("referenceInstanceType_Value") as string;
      const sTitle = oSource.data("title") as string;

      let sPdfFileGuid: string = null;
      if (sInstanceType === "DOCUMENTATION") {
        sPdfFileGuid = await PdfService.createDocumentationPdf(sInstanceGuid);
      } else if (sInstanceType === "ORDER") {
        sPdfFileGuid = await PdfService.createOrderPdf(sInstanceGuid);
      }

      const sPdfFileUrl = FileService.getServiceUrl() + "/" + sPdfFileGuid;
      const oPdfViewer = new PDFViewer({
        source: sPdfFileUrl,
        title: sTitle || "PDF Document", // Fallback title if Description is empty
        height: "600px", // Adjustable based on your UI needs
      });
      oPdfViewer.open();
    } finally {
      this.setAppUnBusy();
    }
  }

  public onRefresh(): void {
    this.getView().getElementBinding().refresh();
  }

  // ObjectHeader Image Add/Delete

  /**
   * Image add event handler/wrapper using FileDetailsController
   * @param {sap.ui.base.Event} oEvent - the navigate to event.
   * @public
   */
  public onImageAdd(oEvent: Event): void {
    const oFileDetailsView = this.getView().byId(
      "FileDetailsViewInvisible",
    ) as XMLView;
    if (oFileDetailsView) {
      (oFileDetailsView.getController() as unknown as FileUploadController).fileUpload();
    }
  }

  /**
   * Image delete event handler/wrapper using FileDetailsController
   * @param {sap.ui.base.Event} oEvent - the navigate to event.
   * @public
   */
  public async onImageDelete(oEvent: Event): Promise<void> {
    const oContext = this.getView().getBindingContext() as Context;
    // todo should the file be actually deleted?
    await oContext.setProperty("LogoFileGuid", null);
    await ODataService.submitBatch();
    oContext.refresh();
  }

  public onAfterBindingChange(sPath: string, oContext: Context): void {
  }

  /* =========================================================== */
  /* event handler layout navigation actions                     */

  /* =========================================================== */

  public onFullScreen(): void {
    const sNextLayout = this.oAppModel.getProperty(
      "/UIState/actionButtonsInfo/midColumn/fullScreen",
    ) as string;

    this.navTo(this._sRouteName, {
      layout: sNextLayout,
      path: encodeURIComponent(this._sPath.substring(1)),
    });
  }

  public onExitFullScreen(): void {
    const sNextLayout = this.oAppModel.getProperty(
      "/UIState/actionButtonsInfo/midColumn/exitFullScreen",
    ) as string;

    this.navTo(this._sRouteName, {
      layout: sNextLayout,
      path: encodeURIComponent(this._sPath.substring(1)),
    });
  }

  public onClose(): void {
    const sNextLayout = this.oAppModel.getProperty(
      "/UIState/actionButtonsInfo/midColumn/closeColumn",
    ) as string;

    if (this._sRouteNameMaster) {
      this.navTo(this._sRouteNameMaster, { layout: sNextLayout });
    }
  }

  public onDelete(oEvent: Event): void {
    // Now call parent's delete method
    if (this.deleteByEvent) {
      this.deleteByEvent(oEvent);
    } else {
      console.log("requires ODataMixin!");
    }
  }

  /* =========================================================== */
  /* public methods                                              */
  /* =========================================================== */

  /**
   * Get the binding path of current view
   * @function
   * @return {string} sPath the current view binding path
   * @private
   */
  /*


      /* =========================================================== */
  /* public formatter                                            */

  /* =========================================================== */

  /**
   * Format view title to allow dynamic language change
   * @returns {string} translated view title text
   * @public
   */
  public formatViewTitle(): string {
    return this.getText(
      "@" + (this._oViewModel.getProperty("/entityType") as string),
    );
  }

  /* =========================================================== */
  /* private methods                                             */

  /* =========================================================== */

  /**
   * Optional hook of subclasses to remove properties from the data of a copied entity (see onCopy).
   */
  protected _cleanCopyData?(oData: Record<string, unknown>): void;

  private _getComponent(): Component {
    return this.getOwnerComponent() as Component;
  }

  /**
   * Set ui model properties from entity related $metadata (ex. EntityType)
   * @function
   * @param {sap.ui.base.Event} oEvent pattern match event in route 'object'
   * @private
   */
  protected _applyServiceMetadata(): void {
    const sType = this._oODataModel
      .getMetaModel()
      .getObject(`/${this._sEntitySet}/$Type`) as string;
    if (!sType) {
      return;
    }
    const sEntityType = sType.split(".")[1]; // sType = schema.entityType
    this._oViewModel.setProperty("/entityType", sEntityType);
    //this._oViewModel.setProperty("/viewTitle", this.getText("@" + sTableHeader));
    this._oViewModel.setProperty(
      "/viewTitle",
      this._getViewTitle(sEntityType),
    );

    this._sEntityType = sEntityType;
  }

  protected _getViewTitle(sEntityType: string): string {
    const sViewTitle = this._oViewModel.getProperty("/viewTitle") as string;
    return this.getText("@" + (sViewTitle ? sViewTitle : sEntityType));
  }

  /**
   * Binds the view to the object path and expands the aggregated line items.
   * @function
   * @param {sap.ui.base.Event} oEvent pattern match event in route 'object'
   * @private
   */
  protected _onObjectMatched(oEvent: Route$PatternMatchedEvent): void {
    const oParameter = oEvent.getParameter("arguments") as Record<string, string>,
      sRouteName = oEvent.getParameter("name");
    if (sRouteName) {
      this._sRouteName = sRouteName;
      this._getComponent().setCurrentRoute(sRouteName);
    }
    for (const value in oParameter) {
      oParameter[value] = decodeURIComponent(oParameter[value]);
    }

    // handle authorisation
    if (!this._oAuthorisation.read) {
      this.display("notAuthorized");
      return;
    }

    // custom binding parameters
    const mParameters =
      this._oBindingConfiguration &&
      this._oBindingConfiguration.parameters
        ? this._oBindingConfiguration
        : {};

    let sPath =
      oParameter.detailPath && oParameter.detailPath != "undefined"
        ? oParameter.detailPath
        : oParameter.path;
    if (sPath) {
      sPath = sPath.startsWith("/") ? sPath : `/${sPath}`;
      this._bindView(sPath, mParameters);
    }

    this._selectFirstObjectPageSection();
  }

  protected _selectFirstObjectPageSection(): void {
    const oObjectPageLayout = this.getView().byId("ObjectPageLayout") as ObjectPageLayout;
    if (![null, undefined].includes(oObjectPageLayout)) {
      const oFirstSection = oObjectPageLayout.getSections()[0];
      oObjectPageLayout.setSelectedSection(oFirstSection);
    }
  }

  /**
   * Get addtional bindings params from Manifest and store for usage inside onBeforeRebinding
   * @private
   */
  protected _applyAdditionalBindingParameters(): void {
    const mData =
        this._oSettings && this._oSettings.additionalBindingParameters
          ? this._oSettings.additionalBindingParameters
          : {},
      mConfig: BindingConfiguration = {};

    // parameters (currently only custom uri params are supported!)
    if (mData.parameters) {
      mConfig.parameters = mData.parameters;
    }

    if (mData.expand) {
      mConfig.expand = mData.expand;
    }

    this._oBindingConfiguration = mConfig;
  }

  /**
   * Binds the view to the object path. Makes sure that detail view displays
   * a busy indicator while data for the corresponding element binding is loaded.
   * @function
   * @param {string} sPath path to the object to be bound to the view.
   * @param {object} mParameters binding params
   * @private
   */
  protected _bindView(sPath: string, mParameters: BindingConfiguration): void {
    if (!sPath) {
      return;
    }

    // Set busy indicator du    ring view binding
    const oView = this.getView();
    const oViewModel = this._oViewModel;

    // If the view was not bound yet its not busy, only if the binding requests data it is set to busy again
    oViewModel.setProperty("/busy", false);

    const oConfig = Object.assign({}, mParameters ? mParameters : {}, {
      path: sPath,
      events: {
        change: () => {
          this._onBindingChange();
        },
        dataRequested: () => {
          oViewModel.setProperty("/busy", true);
        },
        dataReceived: (oEvent: ODataContextBinding$DataReceivedEvent) => {
          oViewModel.setProperty("/busy", false);
          if (oEvent.getParameter("error")) {
            oEvent.cancelBubble();
            this.getRouter().getTargets().display("notFound");
            return;
          }
          this._setInstanceActions();
          this._afterViewBound(sPath, mParameters);
        },
      },
    });

    oView.bindElement(this._validateParametersFromConfig(oConfig));
    (oView as XMLView & { _sPath?: string })._sPath = sPath;
    // needed, otherwise no instance actions loaded, if coming directly from event list report view
    //this.getView().getModel().refresh();
  }

  protected _afterViewBound(sPath?: string, mParameters?: object): void {
  }

  protected _entitySetTypeHasNavigationProperty(
    sEntitySetName: string,
    sNavigationProperty: string,
  ): boolean {
    if (sEntitySetName === undefined) {
      return false;
    }
    const oNavigationPropertyBinding = this.getModel<ODataModel>()
      .getMetaModel()
      .getObject(`/${sEntitySetName}/$NavigationPropertyBinding`) as Record<string, string>;
    return (
      !!oNavigationPropertyBinding &&
      sNavigationProperty in oNavigationPropertyBinding
    );
  }

  protected _setInstanceActions(): void {
    const oContext = this.getView().getBindingContext() as Context;
    const oInstantActions: InstanceActions = {
      grant: false,
      edit: false,
      delete: false,
    };

    if (!oContext) {
      return;
    }
    const sPath = oContext.getPath();
    const sEntitySetName = sPath.match(/\/([a-zA-z]+)\(/)[1];

    if (
      this._entitySetTypeHasNavigationProperty(
        sEntitySetName,
        "InstanceActionDetails",
      )
    ) {
      const arActions = oContext
        .getModel()
        .bindList("InstanceActionDetails", oContext)
        .getContexts();
      arActions.forEach((oActionContext) => {
        const oObject = oActionContext.getObject() as { Type_Value: string };
        if (oObject.Type_Value === "GRANT") {
          oInstantActions.grant = true;
        } else if (oObject.Type_Value === "EDIT") {
          oInstantActions.edit = true;
        } else if (oObject.Type_Value === "DELETE") {
          oInstantActions.delete = true;
        }
      });
      this._oViewModel.setProperty("/InstanceAction", oInstantActions);
    }
  }

  /**
   * Event handler for binding change event
   * @function
   * @param {sap.ui.base.Event} oEvent - the navigate to event.
   * @private
   */
  protected _onBindingChange(): void {
    const oView = this.getView();
    const oElementBinding = oView.getElementBinding();
    const sHash = HashChanger.getInstance().getHash();
    //oAppViewModel = this.getModel("appView");
    if (this._oObjectPage && sHash in this._sRoutedHash) {
      this._oObjectPage.setSelectedSection(this._sRoutedHash[sHash]);
    }
    // No data for the binding
    if (!oElementBinding.getBoundContext()) {
      this._sPath = undefined;

      // needed to be overwritten in case "object not found" page is unwanted:
      if (!this.getModel<ODataModel>().hasPendingChanges()) {
        this._displayDetailObjectNotFound();
      }
      // if object could not be found, the selection in the master list
      // does not make sense anymore.
      //this.getOwnerComponent().oListSelector.clearMasterListSelection();
      return;
    }

    const sPath = oElementBinding.getBoundContext().getPath();

    // fire bindingChange event
    this._sPath = sPath;
    this.onAfterBindingChange(sPath, oElementBinding.getBoundContext() as Context);
  }

  protected _displayDetailObjectNotFound(): void {
    this.display("detailObjectNotFound");
  }

  public onNavigateSaveTarget(oEvent: ObjectPageLayout$NavigateEvent): void {
    const oHashChanger = new HashChanger();
    const oSelectedSection = oEvent.getParameter("section");
    const oObjectPage = oEvent.getSource();
    this._oObjectPage = oObjectPage;
    this._sRoutedHash[oHashChanger.getHash()] = oSelectedSection;
  }

  /**
   * Loads the $count of a navigation property with a separate request and writes it as "(n)" into the ui model,
   * so the collection itself does not have to be expanded just for a section title.
   * @param {string} sPath - path relative to the service url, e.g. "Orders('...')/FileDetails"
   * @param {string} sUiModelProperty - target property in the ui model, e.g. "/AttachmentCount"
   * @param {string} [sFilter] - optional $filter expression, e.g. "Category_Value ne 'RICHTEXT'"
   * @returns {Promise<void>}
   * @protected
   */
  protected async _loadCount(sPath: string, sUiModelProperty: string, sFilter?: string): Promise<void> {
    this._oViewModel.setProperty(sUiModelProperty, "");
    const sQuery = sFilter ? "?$filter=" + encodeURIComponent(sFilter) : "";
    try {
      const iCount = Number(await HttpUtil.get(this.getODataModel().getServiceUrl() + sPath + "/$count" + sQuery));
      if (!Number.isNaN(iCount)) {
        this._oViewModel.setProperty(sUiModelProperty, "(" + iCount + ")");
      }
    } catch (oError) {
      console.error(oError);
    }
  }

  /**
   * Loads the number of attachments shown by the FileDetails fragment (DefaultFileDetails) into "/AttachmentCount".
   * Once the (lazy loaded) fragment is rendered, DefaultFileDetails keeps the value up to date via its countText binding.
   * @param {string} sPath - path of the entity relative to the service url, e.g. "Orders('...')"
   * @returns {Promise<void>}
   * @protected
   */
  protected _loadAttachmentCount(sPath: string): Promise<void> {
    return this._loadCount(sPath + "/FileDetails", "/AttachmentCount",
      DefaultFileDetails.getCountFilter(FILE_DETAILS_CATEGORY_IDS));
  }
}
